'use client'

import { useCallback, useRef, useState } from 'react'
import { Pause, Play, Volume2, VolumeX } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  MINIMAX_H3_CDN,
  MINIMAX_H3_PROMPTS,
  MINIMAX_H3_SOURCES,
  type MinimaxH3AspectRatio,
} from '@/content/marketing/minimax-h3-prompts/prompts'
import { track } from '@/lib/analytics/track'

const ASPECT_CLASS: Record<MinimaxH3AspectRatio, string> = {
  '16:9': 'aspect-video',
  '9:16': 'aspect-[9/16]',
  '1:1': 'aspect-square',
  '4:3': 'aspect-[4/3]',
  '21:9': 'aspect-[21/9]',
}

/**
 * Splits an editing prompt at its `Keep` clause so the preservation list can be
 * called out separately — that clause is the whole technique, and burying it in
 * a paragraph is how people end up rerolling instead of editing.
 */
function splitPrompt(prompt: string) {
  const at = prompt.indexOf('Keep ')
  if (at === -1) return { change: prompt, keep: null }
  return { change: prompt.slice(0, at).trim(), keep: prompt.slice(at).trim() }
}

/** The edits that ship with a rendered sample, grouped under their source clip. */
const PAIRS = Object.entries(MINIMAX_H3_SOURCES)
  .map(([sourceSlug, source]) => ({
    sourceSlug,
    source,
    edits: MINIMAX_H3_PROMPTS.filter(
      p => p.sourceSlug === sourceSlug && p.hasSample,
    ),
  }))
  .filter(group => group.edits.length > 0)

export function MinimaxH3BeforeAfter() {
  const [activeBySource, setActiveBySource] = useState<Record<string, number>>(
    {},
  )
  const [playing, setPlaying] = useState<string | null>(null)
  const [muted, setMuted] = useState(true)
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({})

  const setRef = useCallback(
    (key: string) => (el: HTMLVideoElement | null) => {
      videoRefs.current[key] = el
    },
    [],
  )

  /** Before and after only read as a comparison if they run in lockstep. */
  const togglePair = (sourceSlug: string, editSlug: string) => {
    const before = videoRefs.current[`${sourceSlug}:before`]
    const after = videoRefs.current[`${sourceSlug}:after`]
    if (!before || !after) return

    if (playing === sourceSlug) {
      before.pause()
      after.pause()
      setPlaying(null)
      return
    }

    Object.values(videoRefs.current).forEach(v => v?.pause())
    before.currentTime = 0
    after.currentTime = 0
    void Promise.all([before.play(), after.play()]).catch(() => {
      if (before.paused && after.paused) {
        setPlaying(current => (current === sourceSlug ? null : current))
      }
    })
    setPlaying(sourceSlug)
    track('minimax_h3_before_after_play', { source: sourceSlug, edit: editSlug })
  }

  return (
    <div className="flex flex-col gap-12">
      {PAIRS.map(group => {
        const activeIndex = activeBySource[group.sourceSlug] ?? 0
        const edit = group.edits[activeIndex] ?? group.edits[0]
        const aspect = ASPECT_CLASS[group.source.aspectRatio]
        const { change, keep } = splitPrompt(edit.prompt)
        const isPlaying = playing === group.sourceSlug

        return (
          <div
            key={group.sourceSlug}
            className="overflow-hidden rounded-2xl border border-border bg-card"
          >
            {/* Edit selector — same source clip, different single change */}
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-4">
              <span className="mr-1 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                {group.source.title}
              </span>
              {group.edits.map((candidate, i) => (
                <button
                  key={candidate.slug}
                  type="button"
                  onClick={() => {
                    setActiveBySource(prev => ({
                      ...prev,
                      [group.sourceSlug]: i,
                    }))
                    Object.values(videoRefs.current).forEach(v => v?.pause())
                    setPlaying(null)
                    track('minimax_h3_before_after_switch', {
                      source: group.sourceSlug,
                      edit: candidate.slug,
                    })
                  }}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    i === activeIndex
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'
                  }`}
                >
                  {candidate.title}
                </button>
              ))}
            </div>

            <div className="grid gap-5 p-5 lg:grid-cols-2">
              {(
                [
                  { key: 'before', label: 'Source', slug: group.sourceSlug },
                  { key: 'after', label: 'After the edit', slug: edit.slug },
                ] as const
              ).map(side => (
                <div key={side.key} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <Badge
                      variant={side.key === 'after' ? 'default' : 'outline'}
                      className="rounded-full text-[10px]"
                    >
                      {side.label}
                    </Badge>
                    {side.key === 'after' && (
                      <button
                        type="button"
                        onClick={() => setMuted(!muted)}
                        className="text-muted-foreground transition-colors hover:text-foreground"
                        aria-label={muted ? 'Unmute' : 'Mute'}
                      >
                        {muted ? (
                          <VolumeX className="size-4" />
                        ) : (
                          <Volume2 className="size-4" />
                        )}
                      </button>
                    )}
                  </div>
                  <div
                    className={`group relative overflow-hidden rounded-xl border border-border bg-black ${aspect}`}
                  >
                    <video
                      ref={setRef(`${group.sourceSlug}:${side.key}`)}
                      src={`${MINIMAX_H3_CDN}/${side.slug}.mp4`}
                      // Frame 0 is where an edit is least visible — the poster
                      // is grabbed at the moment the change actually reads.
                      poster={`${MINIMAX_H3_CDN}/${side.slug}.jpg`}
                      // Only the edited side carries audio; two tracks at once
                      // would just phase against each other.
                      muted={side.key === 'before' ? true : muted}
                      loop
                      playsInline
                      preload="metadata"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => togglePair(group.sourceSlug, edit.slug)}
                      className="absolute inset-0 flex items-center justify-center bg-black/10 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                      aria-label={isPlaying ? 'Pause comparison' : 'Play comparison'}
                    >
                      {isPlaying ? (
                        <Pause className="size-10 text-white/85" />
                      ) : (
                        <Play className="size-10 text-white/85" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* The prompt that produced the right-hand clip */}
            <div className="border-t border-border bg-muted/40 px-5 py-4">
              <p className="text-[10px] font-medium tracking-[0.3em] text-muted-foreground uppercase">
                The prompt
              </p>
              <p className="mt-2 text-sm leading-6 text-foreground/80">{change}</p>
              {keep && (
                <p className="mt-2 text-sm leading-6 text-foreground/60">
                  <span className="font-semibold text-foreground/80">
                    {keep.split('.')[0]}.
                  </span>
                  {keep.slice(keep.indexOf('.') + 1)}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
